/**
 * Sensor monitoring capabilities for temperature and humidity sensors
 * Provides conditions and triggers for environmental monitoring
 */

/**
 * Create temperatureRange condition for temperature checking
 */
export function createTemperatureRangeCondition() {
	return {
		label: 'Temperature Range',
		description: 'Check if temperature is within specified range',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'operator',
				label: 'Comparison',
				type: 'select',
				options: [
					{ value: 'eq', label: 'Equals' },
					{ value: 'gt', label: 'Greater than' },
					{ value: 'gte', label: 'Greater or equal' },
					{ value: 'lt', label: 'Less than' },
					{ value: 'lte', label: 'Less or equal' },
					{ value: 'between', label: 'Between' },
				],
				description: 'Temperature comparison operator',
			},
			{
				name: 'temperature',
				label: 'Temperature Value',
				type: 'number',
				min: -40,
				max: 125,
				unit: '°C',
				description: 'Target temperature value in Celsius',
			},
			{
				name: 'temperatureMax',
				label: 'Maximum Temperature',
				type: 'number',
				min: -40,
				max: 125,
				unit: '°C',
				description: 'Maximum temperature for range comparison',
				optional: true,
				showWhen: { parameter: 'operator', value: 'between' },
			},
			{
				name: 'tolerance',
				label: 'Temperature Tolerance',
				type: 'number',
				min: 0.1,
				max: 10,
				unit: '°C',
				description: 'Temperature tolerance (default: 0.5°C)',
				optional: true,
				defaultValue: 0.5,
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const sensorData = deviceState.status?.sensor;
			const currentTemp = sensorData?.tmp || sensorData?.temperature?.tC;

			if (currentTemp === undefined) return false;

			const tolerance = params.tolerance || 0.5;

			switch (params.operator) {
				case 'eq':
					return Math.abs(currentTemp - params.temperature) <= tolerance;
				case 'gt':
					return currentTemp > params.temperature;
				case 'gte':
					return currentTemp >= params.temperature;
				case 'lt':
					return currentTemp < params.temperature;
				case 'lte':
					return currentTemp <= params.temperature;
				case 'between':
					return (
						currentTemp >= params.temperature &&
						currentTemp <= (params.temperatureMax || params.temperature)
					);
				default:
					return false;
			}
		},
	};
}

/**
 * Create humidityRange condition for humidity checking
 */
export function createHumidityRangeCondition() {
	return {
		label: 'Humidity Range',
		description: 'Check if humidity is within specified range',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'operator',
				label: 'Comparison',
				type: 'select',
				options: [
					{ value: 'eq', label: 'Equals' },
					{ value: 'gt', label: 'Greater than' },
					{ value: 'gte', label: 'Greater or equal' },
					{ value: 'lt', label: 'Less than' },
					{ value: 'lte', label: 'Less or equal' },
					{ value: 'between', label: 'Between' },
				],
				description: 'Humidity comparison operator',
			},
			{
				name: 'humidity',
				label: 'Humidity Value',
				type: 'number',
				min: 0,
				max: 100,
				unit: '%',
				description: 'Target humidity percentage',
			},
			{
				name: 'humidityMax',
				label: 'Maximum Humidity',
				type: 'number',
				min: 0,
				max: 100,
				unit: '%',
				description: 'Maximum humidity for range comparison',
				optional: true,
				showWhen: { parameter: 'operator', value: 'between' },
			},
			{
				name: 'tolerance',
				label: 'Humidity Tolerance',
				type: 'number',
				min: 0.1,
				max: 10,
				unit: '%',
				description: 'Humidity tolerance (default: 2%)',
				optional: true,
				defaultValue: 2,
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const sensorData = deviceState.status?.sensor;
			const currentHumidity = sensorData?.hum || sensorData?.humidity?.rH;

			if (currentHumidity === undefined) return false;

			const tolerance = params.tolerance || 2;

			switch (params.operator) {
				case 'eq':
					return Math.abs(currentHumidity - params.humidity) <= tolerance;
				case 'gt':
					return currentHumidity > params.humidity;
				case 'gte':
					return currentHumidity >= params.humidity;
				case 'lt':
					return currentHumidity < params.humidity;
				case 'lte':
					return currentHumidity <= params.humidity;
				case 'between':
					return (
						currentHumidity >= params.humidity &&
						currentHumidity <= (params.humidityMax || params.humidity)
					);
				default:
					return false;
			}
		},
	};
}

/**
 * Create batteryLevel condition for battery monitoring
 */
export function createBatteryLevelCondition() {
	return {
		label: 'Battery Level',
		description: 'Check sensor battery status',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'operator',
				label: 'Comparison',
				type: 'select',
				options: [
					{ value: 'gt', label: 'Greater than' },
					{ value: 'gte', label: 'Greater or equal' },
					{ value: 'lt', label: 'Less than' },
					{ value: 'lte', label: 'Less or equal' },
				],
				description: 'Battery level comparison',
			},
			{
				name: 'batteryLevel',
				label: 'Battery Percentage',
				type: 'number',
				min: 0,
				max: 100,
				unit: '%',
				description: 'Target battery level percentage',
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const sensorData = deviceState.status?.sensor;
			const currentBattery = sensorData?.bat || sensorData?.battery?.V;

			if (currentBattery === undefined) return false;

			// Convert voltage to percentage (approximate)
			const batteryPercentage =
				typeof currentBattery === 'number' && currentBattery > 10
					? Math.min(100, Math.max(0, ((currentBattery - 2.2) / (3.0 - 2.2)) * 100))
					: currentBattery;

			switch (params.operator) {
				case 'gt':
					return batteryPercentage > params.batteryLevel;
				case 'gte':
					return batteryPercentage >= params.batteryLevel;
				case 'lt':
					return batteryPercentage < params.batteryLevel;
				case 'lte':
					return batteryPercentage <= params.batteryLevel;
				default:
					return false;
			}
		},
	};
}

/**
 * Create temperatureChanged trigger for temperature change detection
 */
export function createTemperatureChangedTrigger() {
	return {
		label: 'Temperature Changed',
		description: 'Triggered when temperature changes significantly',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'threshold',
				label: 'Change Threshold',
				type: 'number',
				min: 0.1,
				max: 10,
				unit: '°C',
				description: 'Minimum temperature change to trigger (default: 1°C)',
				optional: true,
				defaultValue: 1,
			},
			{
				name: 'direction',
				label: 'Change Direction',
				type: 'select',
				options: [
					{ value: 'any', label: 'Any change' },
					{ value: 'increase', label: 'Temperature increased' },
					{ value: 'decrease', label: 'Temperature decreased' },
				],
				description: 'Direction of temperature change',
				optional: true,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const oldSensor = oldState?.status?.sensor;
			const newSensor = newState?.status?.sensor;

			const oldTemp = oldSensor?.tmp || oldSensor?.temperature?.tC;
			const newTemp = newSensor?.tmp || newSensor?.temperature?.tC;

			if (oldTemp === undefined || newTemp === undefined) return false;

			const threshold = params.threshold || 1;
			const change = newTemp - oldTemp;
			const absChange = Math.abs(change);

			if (absChange < threshold) return false;

			switch (params.direction) {
				case 'increase':
					return change > 0;
				case 'decrease':
					return change < 0;
				case 'any':
				default:
					return true;
			}
		},
	};
}

/**
 * Create humidityChanged trigger for humidity change detection
 */
export function createHumidityChangedTrigger() {
	return {
		label: 'Humidity Changed',
		description: 'Triggered when humidity changes significantly',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'threshold',
				label: 'Change Threshold',
				type: 'number',
				min: 0.1,
				max: 20,
				unit: '%',
				description: 'Minimum humidity change to trigger (default: 5%)',
				optional: true,
				defaultValue: 5,
			},
			{
				name: 'direction',
				label: 'Change Direction',
				type: 'select',
				options: [
					{ value: 'any', label: 'Any change' },
					{ value: 'increase', label: 'Humidity increased' },
					{ value: 'decrease', label: 'Humidity decreased' },
				],
				description: 'Direction of humidity change',
				optional: true,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const oldSensor = oldState?.status?.sensor;
			const newSensor = newState?.status?.sensor;

			const oldHumidity = oldSensor?.hum || oldSensor?.humidity?.rH;
			const newHumidity = newSensor?.hum || newSensor?.humidity?.rH;

			if (oldHumidity === undefined || newHumidity === undefined) return false;

			const threshold = params.threshold || 5;
			const change = newHumidity - oldHumidity;
			const absChange = Math.abs(change);

			if (absChange < threshold) return false;

			switch (params.direction) {
				case 'increase':
					return change > 0;
				case 'decrease':
					return change < 0;
				case 'any':
				default:
					return true;
			}
		},
	};
}

/**
 * Create temperatureThreshold trigger for threshold crossing detection
 */
export function createTemperatureThresholdTrigger() {
	return {
		label: 'Temperature Threshold',
		description: 'Triggered when temperature crosses a threshold',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'threshold',
				label: 'Temperature Threshold',
				type: 'number',
				min: -40,
				max: 125,
				unit: '°C',
				description: 'Temperature threshold value',
			},
			{
				name: 'direction',
				label: 'Cross Direction',
				type: 'select',
				options: [
					{ value: 'above', label: 'Goes above threshold' },
					{ value: 'below', label: 'Goes below threshold' },
					{ value: 'any', label: 'Crosses in any direction' },
				],
				description: 'Threshold crossing direction',
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const oldSensor = oldState?.status?.sensor;
			const newSensor = newState?.status?.sensor;

			const oldTemp = oldSensor?.tmp || oldSensor?.temperature?.tC;
			const newTemp = newSensor?.tmp || newSensor?.temperature?.tC;

			if (oldTemp === undefined || newTemp === undefined) return false;

			const threshold = params.threshold;
			const oldAbove = oldTemp > threshold;
			const newAbove = newTemp > threshold;

			if (oldAbove === newAbove) return false;

			switch (params.direction) {
				case 'above':
					return !oldAbove && newAbove;
				case 'below':
					return oldAbove && !newAbove;
				case 'any':
					return true;
				default:
					return false;
			}
		},
	};
}

/**
 * Create humidityThreshold trigger for threshold crossing detection
 */
export function createHumidityThresholdTrigger() {
	return {
		label: 'Humidity Threshold',
		description: 'Triggered when humidity crosses a threshold',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'threshold',
				label: 'Humidity Threshold',
				type: 'number',
				min: 0,
				max: 100,
				unit: '%',
				description: 'Humidity threshold percentage',
			},
			{
				name: 'direction',
				label: 'Cross Direction',
				type: 'select',
				options: [
					{ value: 'above', label: 'Goes above threshold' },
					{ value: 'below', label: 'Goes below threshold' },
					{ value: 'any', label: 'Crosses in any direction' },
				],
				description: 'Threshold crossing direction',
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const oldSensor = oldState?.status?.sensor;
			const newSensor = newState?.status?.sensor;

			const oldHumidity = oldSensor?.hum || oldSensor?.humidity?.rH;
			const newHumidity = newSensor?.hum || newSensor?.humidity?.rH;

			if (oldHumidity === undefined || newHumidity === undefined) return false;

			const threshold = params.threshold;
			const oldAbove = oldHumidity > threshold;
			const newAbove = newHumidity > threshold;

			if (oldAbove === newAbove) return false;

			switch (params.direction) {
				case 'above':
					return !oldAbove && newAbove;
				case 'below':
					return oldAbove && !newAbove;
				case 'any':
					return true;
				default:
					return false;
			}
		},
	};
}

/**
 * Create lowBattery trigger for battery warning detection
 */
export function createLowBatteryTrigger() {
	return {
		label: 'Low Battery Warning',
		description: 'Triggered when battery level gets low',
		channels: ['sensor:0'],
		parameters: [
			{
				name: 'batteryThreshold',
				label: 'Battery Threshold',
				type: 'number',
				min: 5,
				max: 50,
				unit: '%',
				description: 'Battery level threshold for low battery warning (default: 20%)',
				optional: true,
				defaultValue: 20,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const oldSensor = oldState?.status?.sensor;
			const newSensor = newState?.status?.sensor;

			const oldBattery = oldSensor?.bat || oldSensor?.battery?.V;
			const newBattery = newSensor?.bat || newSensor?.battery?.V;

			if (oldBattery === undefined || newBattery === undefined) return false;

			// Convert voltage to percentage (approximate)
			const oldPercentage =
				typeof oldBattery === 'number' && oldBattery > 10
					? Math.min(100, Math.max(0, ((oldBattery - 2.2) / (3.0 - 2.2)) * 100))
					: oldBattery;
			const newPercentage =
				typeof newBattery === 'number' && newBattery > 10
					? Math.min(100, Math.max(0, ((newBattery - 2.2) / (3.0 - 2.2)) * 100))
					: newBattery;

			const threshold = params.batteryThreshold || 20;

			// Trigger when battery crosses below threshold
			return oldPercentage >= threshold && newPercentage < threshold;
		},
	};
}
